/**
 * Privacy Settings Page
 * 
 * GDPR compliance features:
 * - Data export (Article 20 - Right to data portability)
 * - Data deletion (Article 17 - Right to erasure)
 * - Privacy policy viewing
 * - Personal data access log
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  requestDataExport,
  requestDataDeletion,
  verifyDataDeletion,
  getPrivacyPolicy,
  getGdprAuditLog,
  type PrivacyPolicy,
} from '../api';
import {
  Button,
  Card,
  PageHeader,
  PageLoading,
  ErrorMessage,
  Badge,
} from '../components';

interface ExportStatus {
  requestId: string;
  status: string;
  expiresAt?: string;
  downloadUrl?: string;
}

interface DeletionStatus {
  requestId: string;
  status: string;
  verificationRequired: boolean;
}

export default function PrivacySettingsPage() {
  const { t } = useTranslation();
  const [privacyPolicy, setPrivacyPolicy] = useState<PrivacyPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Export states
  const [exportLoading, setExportLoading] = useState(false);
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');

  // Deletion states
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);
  const [deletionConfirmed, setDeletionConfirmed] = useState(false);

  // Audit log
  const [auditLog, setAuditLog] = useState<Array<{
    action: string;
    resource_type: string;
    resource_id: string;
    details: string;
    performed_at: string;
  }> | null>(null);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  useEffect(() => {
    loadPrivacyPolicy();
  }, []);

  const loadPrivacyPolicy = async () => {
    try {
      setLoading(true);
      setError(null);
      const policy = await getPrivacyPolicy();
      setPrivacyPolicy(policy);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load privacy policy');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLog = async () => {
    try {
      setAuditLogLoading(true);
      const response = await getGdprAuditLog(50);
      setAuditLog(response.logs);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      setError(null);
      const response = await requestDataExport(exportFormat);
      setExportStatus({
        requestId: response.request_id,
        status: response.status,
        expiresAt: response.expires_at,
        downloadUrl: response.download_url,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request data export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownload = (requestId: string) => {
    // Open download in new window/tab
    const baseUrl = import.meta.env.VITE_API_URL || 'https://kirjuri-backend.tuomas-508.workers.dev';
    window.open(`${baseUrl}/gdpr/export/${requestId}/download`, '_blank');
  };

  const handleDeletionRequest = async () => {
    try {
      setDeletionLoading(true);
      setError(null);
      const response = await requestDataDeletion(deletionReason || undefined);
      setDeletionStatus({
        requestId: response.request_id,
        status: response.status,
        verificationRequired: response.verification_required,
      });
      setShowDeletionConfirm(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request data deletion');
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleVerifyDeletion = async () => {
    if (!deletionStatus?.requestId || !verificationCode) return;

    try {
      setDeletionLoading(true);
      setError(null);
      await verifyDataDeletion(deletionStatus.requestId, verificationCode.toUpperCase());
      setDeletionConfirmed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setDeletionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fi-FI');
  };

  if (loading) {
    return <PageLoading message="Ladataan tietosuoja-asetuksia..." />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <PageHeader
        title="🔒 Tietosuoja"
        subtitle="Hallitse henkilötietojasi ja GDPR-oikeuksiasi"
      />

      {error && (
        <ErrorMessage
          message={error}
          className="mb-6"
        />
      )}

      {/* Privacy Policy Section */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            📜 Tietosuojaseloste
          </h2>
          <Badge variant="info">
            Versio {privacyPolicy?.version}
          </Badge>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Viimeksi päivitetty: {privacyPolicy?.last_updated}
        </p>
        <div className="prose prose-sm max-w-none text-gray-700">
          <p>{privacyPolicy?.policy.description}</p>
          <h4 className="font-medium mt-4 mb-2">Keräämämme tiedot:</h4>
          <ul className="list-disc pl-5 space-y-1">
            {privacyPolicy?.policy.data_we_collect.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <h4 className="font-medium mt-4 mb-2">Oikeutesi:</h4>
          <ul className="list-disc pl-5 space-y-1">
            {privacyPolicy?.policy.your_rights.map((right, index) => (
              <li key={index}>{right}</li>
            ))}
          </ul>
        </div>
        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Rekisterinpitäjä:</strong> {privacyPolicy?.policy.data_controller.name}
            <br />
            <strong>Yhteys:</strong> {privacyPolicy?.policy.data_controller.contact}
          </p>
        </div>
      </Card>

      {/* Data Export Section */}
      <Card className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          📥 Lataa tietosi (GDPR Artikla 20)
        </h2>
        <p className="text-gray-600 mb-4">
          Voit ladata kaikki sinusta keräämämme tiedot koneluettavassa muodossa. 
          Tiedot toimitetaan ZIP-pakettina, joka sisältää käyttäjäprofiilisi, 
          organisaatiotietosi, tapahtumat ja tiedostot.
        </p>

        {!exportStatus ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vientimuoto
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="json"
                    checked={exportFormat === 'json'}
                    onChange={() => setExportFormat('json')}
                    className="mr-2"
                  />
                  <span>JSON (suositeltu)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="csv"
                    checked={exportFormat === 'csv'}
                    onChange={() => setExportFormat('csv')}
                    className="mr-2"
                  />
                  <span>CSV</span>
                </label>
              </div>
            </div>
            <Button
              onClick={handleExport}
              disabled={exportLoading}
              variant="primary"
            >
              {exportLoading ? '⏳ Pyydetään...' : '📥 Pyydä tietojen vienti'}
            </Button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-green-600 text-xl">✅</span>
              <span className="font-medium text-green-900">
                Vienti valmis!
              </span>
            </div>
            <p className="text-sm text-green-800 mb-3">
              Tietojen vienti on käsitelty. Voit ladata tiedot alla olevasta linkistä.
              {exportStatus.expiresAt && (
                <>
                  {' '}Linkki vanhenee{' '}
                  <strong>{formatDate(exportStatus.expiresAt)}</strong>.
                </>
              )}
            </p>
            <Button
              onClick={() => handleDownload(exportStatus.requestId)}
              variant="primary"
              size="sm"
            >
              📥 Lataa tiedot
            </Button>
          </div>
        )}
      </Card>

      {/* Data Deletion Section */}
      <Card className="mb-6 border-red-200">
        <h2 className="text-xl font-semibold text-red-900 mb-4">
          🗑️ Poista tilisi (GDPR Artikla 17)
        </h2>
        <p className="text-red-700 mb-4">
          <strong>Varoitus:</strong> Tämä toiminto poistaa henkilötietosi järjestelmästä. 
          Kirjanpitotiedot säilytetään lakisääteisen velvollisuuden vuoksi, mutta 
          ne anonymisoidaan. Tätä toimintoa ei voi peruuttaa.
        </p>

        {!deletionStatus ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Poiston syy (valinnainen)
              </label>
              <textarea
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                placeholder="Kerro miksi haluat poistaa tilisi..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={3}
              />
            </div>
            <Button
              onClick={() => setShowDeletionConfirm(true)}
              variant="danger"
            >
              🗑️ Aloita tilin poisto
            </Button>
          </div>
        ) : deletionConfirmed ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-green-600 text-xl">✅</span>
              <span className="font-medium text-green-900">
                Tili poistettu
              </span>
            </div>
            <p className="text-sm text-green-800">
              Henkilötietosi on poistettu onnistuneesti. Kirjanpidolliset tiedot 
              on anonymisoitu lakisääteisen säilytysvelvollisuuden vuoksi.
            </p>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-yellow-600 text-xl">⏳</span>
              <span className="font-medium text-yellow-900">
                Vahvistus vaaditaan
              </span>
            </div>
            <p className="text-sm text-yellow-800 mb-3">
              Poistopyyntö on vastaanotettu. Syötä vahvistuskoodi, joka on lähetetty 
              sähköpostiisi, jatkaaksesi poistoa.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Vahvistuskoodi"
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 uppercase"
                maxLength={8}
              />
              <Button
                onClick={handleVerifyDeletion}
                disabled={deletionLoading || verificationCode.length < 4}
                variant="danger"
              >
                {deletionLoading ? '⏳...' : t('privacy.confirmDeletion')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Deletion Confirmation Modal */}
      {showDeletionConfirm && !deletionStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-4">
              ⚠️ Vahvista tilin poisto
            </h3>
            <p className="text-gray-700 mb-4">
              Oletko varma että haluat poistaa tilisi? Tämä toiminto:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-700 mb-6">
              <li>Poistaa käyttäjäprofiilisi</li>
              <li>Poistaa pääsyn kaikkiin organisaatioihin</li>
              <li>Anonymisoi chat-viestisi</li>
              <li>
                <strong>Säilyttää kirjanpitotiedot anonymisoituna</strong> (laki vaatii)
              </li>
            </ul>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeletionConfirm(false)}
              >
                Peruuta
              </Button>
              <Button
                variant="danger"
                onClick={handleDeletionRequest}
                disabled={deletionLoading}
              >
                {deletionLoading ? '⏳ Käsitellään...' : 'Kyllä, poista tilini'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            📋 Tietojen käyttöloki
          </h2>
          <Button
            onClick={loadAuditLog}
            disabled={auditLogLoading}
            variant="secondary"
            size="sm"
          >
            {auditLogLoading ? '⏳...' : '🔄 Päivitä'}
          </Button>
        </div>
        <p className="text-gray-600 mb-4">
          Näet tässä lokitietoja henkilötietojesi käsittelystä.
        </p>

        {auditLog === null ? (
          <div className="text-center py-8 text-gray-500">
            <p>Paina "Päivitä" nähdäksesi käyttölokin</p>
          </div>
        ) : auditLog.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>Ei lokimerkintöjä</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    Toiminto
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    Resurssi
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    Aika
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditLog.map((log, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-gray-900">{log.action}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {log.resource_type} {log.resource_id && `(${log.resource_id})`}
                    </td>
                    <td className="px-4 py-2 text-gray-600">
                      {formatDate(log.performed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
